/**
 * The plan wall graph (PRP-SKETCH-FIELD-3 H12, H13, J15). The drawing is built
 * from one plan-level graph instead of per-room walls:
 *
 * 1. every room edge becomes its wall's CENTERLINE: the edge offset outward by
 *    half its stored thickness, its ends where it meets the neighbouring edges'
 *    centerlines (so walls meet at their centers, not at their faces);
 * 2. centerlines on one line (parallel, within the shared-wall tolerance) are one
 *    wall line; overlapping runs on it are merged;
 * 3. each line is split at every T and X junction: every point where another
 *    wall's centerline ends on it;
 * 4. each segment is kept once, with its thickness, the rooms on each side, its
 *    openings (a shared opening once, `canonicalOpening`) and, for a thick
 *    exterior wall, its outer-face line. A 0 in wall (a fully missing wall) is a
 *    segment of thickness 0 flagged `missing`. A 0 in wall lying on the FACE of a
 *    thicker wall of the room across (H4's one-sided removal: a hall that absorbed
 *    the wall into a living room) is that wall's opening, not a second line: it joins
 *    the thicker wall's segment, which records the face (`face`), and its missing wall
 *    draws once, dotted at that face (W4, DD-SF3.15). The absorbing room's side walls
 *    end at the face. The export geometry is unchanged (DD-SF3.3).
 *
 * Draw order (`DRAW_ORDER`): every room fill, then every segment, then every
 * opening, then labels (`labels.ts`). A door swinging into the room drawn later is
 * therefore never painted over by that room's fill (H12), and a T junction is one
 * straight line with no jog (H13).
 *
 * Prior art: app/services/placement/wall_graph.py (lines clustered by coordinate,
 * runs merged per line, one thickness per wall). Pure; reads the plan only.
 */
import { IN_PER_FT, POLICY, SHARED_TOL_FT, inToFt } from './policy';
import { EPS, along, area, dist, edgeOf, outN, unit } from './geom';
import { wallIn } from './thickness';
import { adjacent, openGeom, sharedMap, type OpenGeom } from './shared';
import { buildUF, edgeKnown } from './uf';
import { canonicalOpening, type OpeningRef } from './doorsShared';
import type { OpeningKind, Plan, Pt, Room } from './types';

/** The layers, in the order they are drawn. */
export const DRAW_ORDER = ['fills', 'segments', 'openings', 'labels'] as const;

/** One room edge's share of a segment. `side` is +1 when the room is on the segment's `N` side. */
export interface GraphSide { rid: string; vid: number; side: 1 | -1 }

export interface GraphSegment {
  id: number;
  /** Centerline ends; a -> b runs along `u`. */
  a: Pt;
  b: Pt;
  u: Pt;
  /** Left normal of `u`; rooms are on its + or - side. */
  N: Pt;
  thickFt: number;
  thickIn: number;
  /** A 0 in wall (a fully missing wall, the rooms meet). */
  missing: boolean;
  sides: GraphSide[];
  /** Room ids on the +N and the -N side. */
  plus: string[];
  minus: string[];
  /** Rooms on one side only (an outside wall). */
  exterior: boolean;
  /** The outer-face line of an exterior wall thicker than the plan's interior default (E19). */
  outer: [Pt, Pt] | null;
  /** Every room edge on it is measured (solid; otherwise drawn rough). */
  known: boolean;
  /** Stretches openings take, as [from, to] distances from `a` (an opening crossing a junction cuts both). */
  cuts: [number, number][];
  /** Indices into `WallGraph.openings` whose middle is on this segment. */
  openings: number[];
  /** W4: a removed wall absorbed into this one lies on its face here, feet along `N` from the centerline. */
  face?: number;
}

export interface GraphOpening extends OpeningRef {
  kind: OpeningKind;
  /** The other copy of a shared opening. */
  partner?: OpeningRef;
  link?: number;
  seg: number;
  /** The opening's ends on the wall's centerline. */
  p0: Pt;
  p1: Pt;
  /** Face geometry of the copy drawn (a door's leaf and arc are in the room it swings into). */
  geom: OpenGeom | null;
}

export interface RoomFill { rid: string; pts: Pt[] }

export interface WallGraph {
  fills: RoomFill[];
  segments: GraphSegment[];
  openings: GraphOpening[];
  /** Points where a wall ends on another wall's line (T and X junctions). */
  junctions: Pt[];
}

/** One room edge's centerline run. */
interface Run {
  rid: string; vid: number; ri: number; p0: Pt; p1: Pt; t: number; n: Pt; u: Pt; known: boolean;
  line?: number; s0?: number; s1?: number; side?: 1 | -1;
  /** W4: a 0 in run moved onto the thicker wall it lies on the face of: its own ends (on the face). */
  faceAt?: [Pt, Pt];
}
interface Line { u: Pt; N: Pt; c: number; runs: Run[] }

const dot = (a: Pt, b: Pt) => a.x * b.x + a.y * b.y;
const cross = (a: Pt, b: Pt) => a.x * b.y - a.y * b.x;
const add = (p: Pt, d: Pt, k: number): Pt => ({ x: p.x + d.x * k, y: p.y + d.y * k });

/** Where offset lines (p, u) and (q, v) meet; null when parallel. */
function meet(p: Pt, u: Pt, q: Pt, v: Pt): Pt | null {
  const den = cross(u, v);
  if (Math.abs(den) < EPS) return null;
  const k = cross({ x: q.x - p.x, y: q.y - p.y }, v) / den;
  return add(p, u, k);
}

/** The centerline runs of one room: each edge offset by half its thickness, ends at its neighbours' lines. */
function roomRuns(r: Room, ri: number): Run[] {
  const n = r.corners.length, A = area(r), U = buildUF(r);
  const edges = r.corners.map((a, i) => {
    const b = r.corners[(i + 1) % n];
    const len = dist(a, b);
    if (len < EPS) return null;
    const nO = outN(r, a, b, A), t = inToFt(wallIn(r, a.id));
    return { a, b, u: unit(a, b), n: nO, t, q: add(a, nO, t / 2), known: edgeKnown(U, a, b) };
  });
  const live = edges.filter((e): e is NonNullable<typeof e> => !!e);
  const m = live.length;
  return live.map((e, k) => {
    const pv = live[(k - 1 + m) % m], nx = live[(k + 1) % m];
    const p0 = meet(e.q, e.u, pv.q, pv.u) ?? add(e.a, e.n, e.t / 2);
    const p1 = meet(e.q, e.u, nx.q, nx.u) ?? add(e.b, e.n, e.t / 2);
    return { rid: r.id, vid: e.a.id, ri, p0, p1, t: e.t, n: e.n, u: e.u, known: e.known };
  });
}

/**
 * W4 (DD-SF3.15): a 0 in run lying on the face of a thicker run of another room,
 * facing it, moves onto that run's centerline and takes its thickness (it joins that
 * wall's segment); `faceAt` keeps a point of its own line, where its opening draws.
 */
function onFaces(runs: Run[]): void {
  for (const z of runs) {
    if (z.t > EPS) continue;
    const w = runs.find((x) => x.rid !== z.rid && x.t > EPS && dot(x.n, z.n) <= POLICY.facing_dot_max
      && Math.abs(dot(x.n, z.p0) - (dot(x.n, x.p0) - x.t / 2)) <= SHARED_TOL_FT
      && overlap(x, z) > EPS);
    if (!w) continue;
    z.faceAt = [z.p0, z.p1];
    z.p0 = add(z.p0, w.n, w.t / 2);
    z.p1 = add(z.p1, w.n, w.t / 2);
    z.t = w.t;
  }
}

/** How much two parallel runs overlap along the first one's direction, feet. */
function overlap(a: Run, b: Run): number {
  const s = (p: Pt) => dot(a.u, p);
  const lo = Math.max(Math.min(s(a.p0), s(a.p1)), Math.min(s(b.p0), s(b.p1)));
  const hi = Math.min(Math.max(s(a.p0), s(a.p1)), Math.max(s(b.p0), s(b.p1)));
  return hi - lo;
}

/** Canonical direction of a line: pointing +x, or +y when vertical. */
function canonDir(u: Pt): Pt {
  return u.x > EPS || (Math.abs(u.x) <= EPS && u.y > 0) ? u : { x: -u.x, y: -u.y };
}

function clusterLines(runs: Run[]): Line[] {
  const lines: Line[] = [];
  for (const run of runs) {
    const u = canonDir(run.u);
    const N = { x: -u.y, y: u.x };
    const c = dot(N, run.p0);
    let li = lines.findIndex((L) => dot(L.u, u) >= POLICY.parallel_dot_min && Math.abs(L.c - c) <= SHARED_TOL_FT);
    if (li < 0) { lines.push({ u, N, c, runs: [] }); li = lines.length - 1; }
    const L = lines[li];
    const s0 = dot(L.u, run.p0), s1 = dot(L.u, run.p1);
    run.line = li;
    run.s0 = Math.min(s0, s1);
    run.s1 = Math.max(s0, s1);
    // The room is on the side opposite its outward normal.
    run.side = dot(run.n, L.N) < 0 ? 1 : -1;
    L.runs.push(run);
  }
  return lines;
}

const at = (L: Line, s: number): Pt => ({ x: L.u.x * s + L.N.x * L.c, y: L.u.y * s + L.N.y * L.c });

/** Sorted, de-duplicated within the tolerance. */
function uniqSorted(v: number[]): number[] {
  const s = [...v].sort((a, b) => a - b);
  return s.filter((x, i) => i === 0 || x - s[i - 1] > SHARED_TOL_FT);
}

/** Junction positions along line `L`: ends of other lines' runs lying on it (T and X). */
function junctionsOn(L: Line, li: number, all: Run[]): number[] {
  const out: number[] = [];
  for (const run of all) {
    if (run.line === li) continue;
    // A run moved onto a face (W4) meets other walls where it was: at the face, the absorbing room's corners.
    for (const p of run.faceAt ?? [run.p0, run.p1]) if (Math.abs(dot(L.N, p) - L.c) <= SHARED_TOL_FT) out.push(dot(L.u, p));
  }
  return out;
}

function segmentsOf(L: Line, li: number, all: Run[], interiorIn: number, out: GraphSegment[], junctionPts: Pt[]): void {
  const J = uniqSorted(junctionsOn(L, li, all));
  const ends = L.runs.flatMap((r) => [r.s0!, r.s1!]);
  const lo = Math.min(...ends), hi = Math.max(...ends);
  const cuts = uniqSorted([...ends, ...J.filter((s) => s > lo + SHARED_TOL_FT && s < hi - SHARED_TOL_FT)]);
  const isJ = (s: number) => J.some((j) => Math.abs(j - s) <= SHARED_TOL_FT);
  type Atom = { s0: number; s1: number; runs: Run[]; sig: string };
  const atoms: Atom[] = [];
  for (let k = 0; k + 1 < cuts.length; k++) {
    const s0 = cuts[k], s1 = cuts[k + 1], mid = (s0 + s1) / 2;
    const runs = L.runs.filter((r) => r.s0! <= mid && r.s1! >= mid);
    if (!runs.length) continue;
    const t = Math.max(...runs.map((r) => r.t));
    const sig = `${[...new Set(runs.map((r) => `${r.rid}:${r.side}`))].sort().join('|')}@${Math.round(t * 1e6)}`;
    const prev = atoms[atoms.length - 1];
    // Merged across a boundary only when nothing meets the wall there and the same rooms line it.
    if (prev && prev.sig === sig && Math.abs(prev.s1 - s0) <= EPS && !isJ(s0)) {
      prev.s1 = s1;
      runs.forEach((r) => { if (!prev.runs.includes(r)) prev.runs.push(r); });
    } else atoms.push({ s0, s1, runs, sig });
  }
  for (const s of J) if (s > lo + SHARED_TOL_FT && s < hi - SHARED_TOL_FT) junctionPts.push(at(L, s));
  for (const a of atoms) {
    const t = Math.max(...a.runs.map((r) => r.t));
    const sides: GraphSide[] = a.runs.map((r) => ({ rid: r.rid, vid: r.vid, side: r.side! }));
    const plus = [...new Set(sides.filter((x) => x.side > 0).map((x) => x.rid))];
    const minus = [...new Set(sides.filter((x) => x.side < 0).map((x) => x.rid))];
    const exterior = !plus.length || !minus.length;
    const thickIn = t * IN_PER_FT;
    const A = at(L, a.s0), B = at(L, a.s1);
    const away = plus.length ? -1 : 1; // the side with no room
    const outer: [Pt, Pt] | null = exterior && thickIn > interiorIn + EPS
      ? [add(A, L.N, (away * t) / 2), add(B, L.N, (away * t) / 2)] : null;
    const seg: GraphSegment = {
      id: out.length, a: A, b: B, u: L.u, N: L.N, thickFt: t, thickIn, missing: t < EPS,
      sides, plus, minus, exterior, outer, known: a.runs.every((r) => r.known), cuts: [], openings: [],
    };
    const fr = a.runs.find((r) => r.faceAt);
    if (fr) seg.face = dot(L.N, fr.faceAt![0]) - L.c;
    out.push(seg);
  }
}

/** Build the plan's wall graph (J15). */
export function buildWallGraph(plan: Plan): WallGraph {
  const runs = plan.rooms.flatMap((r, ri) => roomRuns(r, ri));
  onFaces(runs);
  const lines = clusterLines(runs);
  const interiorIn = plan.defaults.interiorWallIn ?? POLICY.interior_wall_in;
  const segments: GraphSegment[] = [];
  const junctions: Pt[] = [];
  const segLine: number[] = [];
  lines.forEach((L, li) => {
    const before = segments.length;
    segmentsOf(L, li, runs, interiorIn, segments, junctions);
    for (let k = before; k < segments.length; k++) segLine.push(li);
  });
  const runOf = new Map(runs.map((r) => [`${r.rid}:${r.vid}`, r]));
  const M = sharedMap(plan);
  const openings: GraphOpening[] = [];
  for (const r of plan.rooms) {
    const A = area(r);
    for (const o of r.openings) {
      const c = canonicalOpening(plan, r, o, M);
      if (c.o !== o) continue;
      const run = runOf.get(`${r.id}:${o.edgeStart}`), E = edgeOf(r, o.edgeStart);
      if (!run || !E) continue;
      const L = lines[run.line!];
      const f0 = along(E.a, E.b, o.offFt), f1 = along(E.a, E.b, o.offFt + o.widthFt);
      const s0 = Math.min(dot(L.u, f0), dot(L.u, f1)), s1 = Math.max(dot(L.u, f0), dot(L.u, f1));
      const mid = (s0 + s1) / 2;
      let seg = -1;
      segments.forEach((g, gi) => {
        if (segLine[gi] !== run.line) return;
        const g0 = dot(L.u, g.a), g1 = dot(L.u, g.b);
        const lo = Math.max(s0, g0), hi = Math.min(s1, g1);
        if (hi - lo > EPS) g.cuts.push([lo - g0, hi - g0]);
        if (mid >= g0 - EPS && mid <= g1 + EPS && seg < 0) seg = gi;
      });
      if (seg < 0) continue;
      const P = adjacent(plan, r, o, M);
      const go: GraphOpening = {
        rid: r.id, oid: o.id, kind: o.kind, seg, p0: at(L, s0), p1: at(L, s1), geom: openGeom(r, o, M, A),
      };
      if (o.link != null) go.link = o.link;
      if (P) go.partner = { rid: P.r.id, oid: P.o.id };
      segments[seg].openings.push(openings.length);
      openings.push(go);
    }
  }
  // A junction lies on two or more lines (an X, two T's at one point): listed once.
  const uniq = junctions.filter((p, i) => junctions.findIndex((q) => dist(p, q) <= SHARED_TOL_FT) === i);
  return { fills: plan.rooms.map((r) => ({ rid: r.id, pts: r.corners.map((p) => ({ x: p.x, y: p.y })) })), segments, openings, junctions: uniq };
}

/** A segment's length, feet. */
export const segmentLength = (g: GraphSegment): number => dist(g.a, g.b);
